import {
    createColumns,
    DataTableCustomized,
} from '@/components/ui/data-table-customized'
import { CHART_COLORS } from '@/constants/chartColors'
import { formatCurrency, formatNumeral } from '@/utils/function'
import dayjs from 'dayjs'
import { Download } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import * as XLSX from 'xlsx'

const COLORS = [
    CHART_COLORS.PRIMARY_BLUE,
    CHART_COLORS.EMERALD_GREEN,
    CHART_COLORS.AMBER_ORANGE,
    CHART_COLORS.INDIGO_PURPLE,
    CHART_COLORS.PINK_FUCHSIA,
    CHART_COLORS.BLUE_GRAY,
    CHART_COLORS.ROSE_RED,
]

export interface ServiceData {
    product_group_name: string
    total_revenue: number
    revenue_share: number
    renewal_revenue: number
    new_sale_revenue: number
    upsell_revenue: number
    customer_count: number
    avg_revenue_per_customer: number
}

interface ServiceBreakdownTableProps {
    data: ServiceData[]
    comparisonData?: ServiceData[]
    periodLabel?: string
    hasComparison?: boolean
    isComparisonTable?: boolean
}

const ServiceBreakdownTable = ({
    data,
    comparisonData,
    periodLabel,
    hasComparison = false,
    isComparisonTable = false,
}: ServiceBreakdownTableProps) => {
    const { t } = useTranslation()

    const getComparisonService = (productGroupName: string) => {
        if (!comparisonData) return null
        return comparisonData.find(
            (s) => s.product_group_name === productGroupName
        )
    }

    const renderChangeIndicator = (
        current: number,
        previous: number | undefined
    ) => {
        if (!hasComparison || previous === undefined || previous === 0)
            return null
        const changePercent = ((current - previous) / previous) * 100
        const isPositive = changePercent >= 0
        return (
            <span
                className={`text-xs ml-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}
            >
                ({isPositive ? '+' : ''}
                {changePercent.toFixed(1)}%)
            </span>
        )
    }

    const exportTableToExcel = () => {
        if (!data || data.length === 0) return

        const exportData = data.map((service) => ({
            [t('employees.performance.service')]:
                service.product_group_name || 'N/A',
            [t('employees.performance.total_revenue')]: service.total_revenue,
            [t('employees.performance.revenue_share')]:
                `${formatNumeral(service.revenue_share)}%`,
            [t('employees.performance.renewal')]: service.renewal_revenue,
            [t('employees.performance.new_sale')]: service.new_sale_revenue,
            [t('employees.performance.upsell')]: service.upsell_revenue,
            [t('employees.performance.total_customer')]: service.customer_count,
            [t('employees.performance.avg_revenue_per_customer')]:
                service.avg_revenue_per_customer,
        }))

        const ws = XLSX.utils.json_to_sheet(exportData)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Service Breakdown')
        XLSX.writeFile(
            wb,
            `service-breakdown-${periodLabel || dayjs().format('YYYY-MM-DD')}.xlsx`
        )
    }

    const columns = useMemo(
        () =>
            createColumns<ServiceData & { _index: number }>([
                {
                    key: 'product_group_name',
                    title: t('employees.performance.service'),
                    width: 200,
                    headerClassName: 'text-left',
                    cellClassName: 'font-medium',
                    render: (value, row) => (
                        <div className="flex items-center gap-2">
                            <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{
                                    backgroundColor:
                                        COLORS[row._index % COLORS.length],
                                }}
                            />
                            <span>{value || 'N/A'}</span>
                        </div>
                    ),
                },
                {
                    key: 'total_revenue',
                    title: t('employees.performance.total_revenue'),
                    width: 150,
                    headerClassName: 'text-right bg-blue-50',
                    cellClassName: 'text-right font-semibold bg-blue-50/50',
                    render: (value, row) => {
                        const compService = isComparisonTable
                            ? null
                            : getComparisonService(row.product_group_name)
                        return (
                            <span>
                                {formatCurrency(value)}
                                {renderChangeIndicator(
                                    value,
                                    compService?.total_revenue
                                )}
                            </span>
                        )
                    },
                },
                {
                    key: 'revenue_share',
                    title: t('employees.performance.revenue_share'),
                    width: 100,
                    headerClassName: 'text-right',
                    cellClassName: 'text-right font-medium',
                    render: (value) => `${formatNumeral(value)}%`,
                },
                {
                    key: 'renewal_revenue',
                    title: t('employees.performance.renewal'),
                    width: 130,
                    headerClassName: 'text-right',
                    cellClassName: 'text-right',
                    render: (value, row) => {
                        const compService = isComparisonTable
                            ? null
                            : getComparisonService(row.product_group_name)
                        return (
                            <span>
                                {formatCurrency(value)}
                                {renderChangeIndicator(
                                    value,
                                    compService?.renewal_revenue
                                )}
                            </span>
                        )
                    },
                },
                {
                    key: 'new_sale_revenue',
                    title: t('employees.performance.new_sale'),
                    width: 130,
                    headerClassName: 'text-right',
                    cellClassName: 'text-right',
                    render: (value, row) => {
                        const compService = isComparisonTable
                            ? null
                            : getComparisonService(row.product_group_name)
                        return (
                            <span>
                                {formatCurrency(value)}
                                {renderChangeIndicator(
                                    value,
                                    compService?.new_sale_revenue
                                )}
                            </span>
                        )
                    },
                },
                {
                    key: 'upsell_revenue',
                    title: t('employees.performance.upsell'),
                    width: 130,
                    headerClassName: 'text-right',
                    cellClassName: 'text-right',
                    render: (value, row) => {
                        const compService = isComparisonTable
                            ? null
                            : getComparisonService(row.product_group_name)
                        return (
                            <span>
                                {formatCurrency(value)}
                                {renderChangeIndicator(
                                    value,
                                    compService?.upsell_revenue
                                )}
                            </span>
                        )
                    },
                },
                {
                    key: 'customer_count',
                    title: t('employees.performance.total_customer'),
                    width: 100,
                    headerClassName: 'text-right',
                    cellClassName: 'text-right',
                    render: (value, row) => {
                        const compService = isComparisonTable
                            ? null
                            : getComparisonService(row.product_group_name)
                        return (
                            <span>
                                {value}
                                {renderChangeIndicator(
                                    value,
                                    compService?.customer_count
                                )}
                            </span>
                        )
                    },
                },
                {
                    key: 'avg_revenue_per_customer',
                    title: t('employees.performance.avg_revenue_per_customer'),
                    width: 130,
                    headerClassName: 'text-right',
                    cellClassName: 'text-right',
                    render: (value, row) => {
                        const compService = isComparisonTable
                            ? null
                            : getComparisonService(row.product_group_name)
                        return (
                            <span>
                                {formatCurrency(value)}
                                {renderChangeIndicator(
                                    value,
                                    compService?.avg_revenue_per_customer
                                )}
                            </span>
                        )
                    },
                },
            ]),
        [t, isComparisonTable, hasComparison, comparisonData]
    )

    // Add index to rows for color mapping
    const rowsWithIndex = useMemo(
        () => data.map((row, index) => ({ ...row, _index: index })),
        [data]
    )

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                {hasComparison && periodLabel ? (
                    <Typogaphy
                        size="small"
                        className={`font-medium ${isComparisonTable ? 'text-gray-500' : 'text-gray-700'}`}
                    >
                        {periodLabel}
                    </Typogaphy>
                ) : (
                    <div />
                )}
                <Button
                    size="small"
                    variant="text"
                    onClick={exportTableToExcel}
                    className="!p-1"
                >
                    <Download className="w-4 h-4" />
                </Button>
            </div>
            <DataTableCustomized
                columns={columns}
                rows={rowsWithIndex}
                rowKey={(row) => row.product_group_name}
                size="small"
            />
        </div>
    )
}

export default ServiceBreakdownTable
